import { Form, Formik, Field } from 'formik';
import { useState } from 'react';

import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import FormikCustomInput from '@perpay-web/components/base/FormikCustomInput/FormikCustomInput';
import { maxLength } from '@perpay-web/fintech/validators/fieldBased';
import { useMountAndUnmount } from '@perpay-web/hooks/useMountAndUnmount';
import { required, combined } from '@perpay-web/validators/fieldBased';

import styles from './CompanySelfOnboard.scss';

const CompanySelfOnboard = ({
    children,
    clearSelfOnboardingCompanyResults,
    error = null,
    initialValue = '',
    isLoadingResults,
    onSelfOnboardingInput,
    onSelfOnboardingOpen,
    onSubmit,
    otherCompany,
    selfOnboardingMatches,
}) => {
    const [selectedSelfOnboardingCompany, setSelectedSelfOnboardingCompany] =
        useState(null);
    useMountAndUnmount(() => {
        onSelfOnboardingOpen(initialValue);
        return () => clearSelfOnboardingCompanyResults();
    });

    const canSubmit = otherCompany.uuid || selectedSelfOnboardingCompany;

    const onClickContinue = (values) => {
        let payload = {};
        if (selectedSelfOnboardingCompany) {
            payload = {
                uuid: selectedSelfOnboardingCompany.uuid,
                isGigEconomyCompany:
                    selectedSelfOnboardingCompany.isGigEconomyCompany,
                hasPerpaySplitPayroll:
                    selectedSelfOnboardingCompany.hasPerpaySplitPayroll,
            };
        } else {
            payload = {
                name: otherCompany.name,
                uuid: otherCompany.uuid,
                selfOnboardingData: values,
            };
        }
        payload.displayName = values.companyName;
        onSubmit(payload);
    };

    const renderSelfOnboardingMatches = (matches, setFieldValue) => (
        <div className={styles.results}>
            <p className={styles.results__copy}>
                {matches.length > 1
                    ? 'We found the following companies. Are any of them your employer?'
                    : 'We found the following company. Is it your employer?'}
            </p>
            <ul className={styles.results__list}>
                {matches.map((match) => (
                    <li key={match.text}>
                        <CustomButton
                            action='tertiary'
                            className={styles.result}
                            onClick={() => {
                                setSelectedSelfOnboardingCompany({
                                    uuid: match.uuid,
                                    isGigEconomyCompany:
                                        match.isGigEconomyCompany,
                                    hasPerpaySplitPayroll:
                                        match.hasPerpaySplitPayroll,
                                });
                                setFieldValue('companyName', match.text);
                                clearSelfOnboardingCompanyResults();
                            }}
                        >
                            {match.text}
                        </CustomButton>
                    </li>
                ))}
            </ul>
        </div>
    );

    const renderForm = (setFieldValue) => (
        <>
            <Field
                name='companyName'
                validate={combined([required, maxLength(100, 'characters')])}
            >
                {(fieldProps) => {
                    const field = {
                        ...fieldProps.field,
                        onInput: (e) => {
                            setSelectedSelfOnboardingCompany(null);
                            setFieldValue('companyName', e.target.value);
                            onSelfOnboardingInput(e.target.value);
                            if (fieldProps.field.onInput) {
                                fieldProps.field.onInput(e);
                            }
                        },
                        errors: error || fieldProps.errors,
                    };
                    return (
                        <FormikCustomInput
                            {...fieldProps}
                            field={field}
                            type='text'
                            label='Company Name'
                            validate={maxLength(100, 'characters')}
                        />
                    );
                }}
            </Field>
            {!selectedSelfOnboardingCompany && selfOnboardingMatches.length
                ? renderSelfOnboardingMatches(
                      selfOnboardingMatches,
                      setFieldValue,
                  )
                : null}
            <Field
                name='companyUrl'
                component={FormikCustomInput}
                type='text'
                label='Company Website (optional)'
                validate={maxLength(250, 'characters')}
            />
        </>
    );

    return (
        <Formik
            onSubmit={(values) => {
                const newValues = { ...values };
                if (values.companyUrl === '') {
                    delete newValues.companyUrl;
                }
                onClickContinue(newValues);
            }}
            initialValues={{ companyName: initialValue }}
            enableReinitialize
        >
            {({ setFieldValue }) => (
                <Form className={styles.form}>
                    {children({
                        canSubmit,
                        isLoading: isLoadingResults,
                        renderForm: () => renderForm(setFieldValue),
                    })}
                </Form>
            )}
        </Formik>
    );
};

export default CompanySelfOnboard;
